/**
 * SF-156: mobile Dossier sort options for the Resources/Military tabs — combined key+direction
 * `<select>` options (mirrors `MobileTargetFinderShell.tsx`'s `SORT_OPTIONS` shape), the null-
 * partition comparator, and the per-option accessor map (Application Interface's "Mobile sort"
 * revision).
 *
 * Null-handling: for every option, a row missing the underlying field sorts to a defined last
 * position *regardless of direction* — missing data sinks to the bottom whether the list is
 * high-to-low or low-to-high. Values are compared via `localeCompare` for strings, numeric
 * subtraction for numbers.
 */

import type { DossierProvinceRow } from '../types/dossier';
import type { CrystalBallData, SpyOnMilitaryData } from '../types/intel';
import { fuzzyMidpointDouble } from './fuzzyValue';
import { latestReportTick } from './dossierFreshness';

export type SortDirection = 'asc' | 'desc';

interface SortOptionDef {
  value: string;
  label: string;
  direction: SortDirection;
}

function cb(row: DossierProvinceRow): CrystalBallData | null {
  return (row.latestCb?.data as CrystalBallData | undefined) ?? null;
}

function som(row: DossierProvinceRow): SpyOnMilitaryData | null {
  return (row.latestSom?.data as SpyOnMilitaryData | undefined) ?? null;
}

type Accessor = (row: DossierProvinceRow) => number | string | null;

// ---- Resources ----

export const RESOURCES_SORT_OPTIONS = [
  { value: 'province-asc', label: 'Province (A–Z)', direction: 'asc' },
  { value: 'land-desc', label: 'Land (High to Low)', direction: 'desc' },
  { value: 'land-asc', label: 'Land (Low to High)', direction: 'asc' },
  { value: 'networth-desc', label: 'Net Worth (High to Low)', direction: 'desc' },
  { value: 'networth-asc', label: 'Net Worth (Low to High)', direction: 'asc' },
  { value: 'gold-desc', label: 'Gold (High to Low)', direction: 'desc' },
  { value: 'gold-asc', label: 'Gold (Low to High)', direction: 'asc' },
  { value: 'food-desc', label: 'Food (High to Low)', direction: 'desc' },
  { value: 'food-asc', label: 'Food (Low to High)', direction: 'asc' },
  { value: 'runes-desc', label: 'Runes (High to Low)', direction: 'desc' },
  { value: 'runes-asc', label: 'Runes (Low to High)', direction: 'asc' },
  { value: 'runesPerAcre-desc', label: 'Runes/Acre (High to Low)', direction: 'desc' },
  { value: 'runesPerAcre-asc', label: 'Runes/Acre (Low to High)', direction: 'asc' },
  { value: 'peasants-desc', label: 'Peasants (High to Low)', direction: 'desc' },
  { value: 'peasants-asc', label: 'Peasants (Low to High)', direction: 'asc' },
  { value: 'soldiers-desc', label: 'Soldiers (High to Low)', direction: 'desc' },
  { value: 'soldiers-asc', label: 'Soldiers (Low to High)', direction: 'asc' },
  { value: 'tradeBalance-desc', label: 'Trade Balance (High to Low)', direction: 'desc' },
  { value: 'tradeBalance-asc', label: 'Trade Balance (Low to High)', direction: 'asc' },
  { value: 'freshness-desc', label: 'Freshness (Newest First)', direction: 'desc' },
  { value: 'freshness-asc', label: 'Freshness (Oldest First)', direction: 'asc' },
] as const satisfies readonly SortOptionDef[];

export type ResourcesSortValue = (typeof RESOURCES_SORT_OPTIONS)[number]['value'];

const RESOURCES_ACCESSORS: Record<ResourcesSortValue, Accessor> = {
  'province-asc': (r) => r.provinceName,
  'land-desc': (r) => r.land,
  'land-asc': (r) => r.land,
  'networth-desc': (r) => r.networth,
  'networth-asc': (r) => r.networth,
  'gold-desc': (r) => cb(r)?.money ?? null,
  'gold-asc': (r) => cb(r)?.money ?? null,
  'food-desc': (r) => cb(r)?.food ?? null,
  'food-asc': (r) => cb(r)?.food ?? null,
  'runes-desc': (r) => cb(r)?.runes ?? null,
  'runes-asc': (r) => cb(r)?.runes ?? null,
  'runesPerAcre-desc': (r) => {
    const c = cb(r);
    return c && c.totalLand > 0 ? c.runes / c.totalLand : null;
  },
  'runesPerAcre-asc': (r) => {
    const c = cb(r);
    return c && c.totalLand > 0 ? c.runes / c.totalLand : null;
  },
  'peasants-desc': (r) => cb(r)?.peasants ?? null,
  'peasants-asc': (r) => cb(r)?.peasants ?? null,
  'soldiers-desc': (r) => cb(r)?.soldiers ?? null,
  'soldiers-asc': (r) => cb(r)?.soldiers ?? null,
  'tradeBalance-desc': (r) => cb(r)?.tradeBalance ?? null,
  'tradeBalance-asc': (r) => cb(r)?.tradeBalance ?? null,
  'freshness-desc': (r) => latestReportTick(r),
  'freshness-asc': (r) => latestReportTick(r),
};

// ---- Military ----

export const MILITARY_SORT_OPTIONS = [
  { value: 'province-asc', label: 'Province (A–Z)', direction: 'asc' },
  { value: 'land-desc', label: 'Land (High to Low)', direction: 'desc' },
  { value: 'land-asc', label: 'Land (Low to High)', direction: 'asc' },
  { value: 'nwpa-desc', label: 'NW/Acre (High to Low)', direction: 'desc' },
  { value: 'nwpa-asc', label: 'NW/Acre (Low to High)', direction: 'asc' },
  { value: 'rawOff-desc', label: 'Raw Off. (High to Low)', direction: 'desc' },
  { value: 'rawOff-asc', label: 'Raw Off. (Low to High)', direction: 'asc' },
  { value: 'ome-desc', label: 'OME (High to Low)', direction: 'desc' },
  { value: 'ome-asc', label: 'OME (Low to High)', direction: 'asc' },
  { value: 'modOff-desc', label: 'Mod. Off. (High to Low)', direction: 'desc' },
  { value: 'modOff-asc', label: 'Mod. Off. (Low to High)', direction: 'asc' },
  { value: 'rawDef-desc', label: 'Raw Def. (High to Low)', direction: 'desc' },
  { value: 'rawDef-asc', label: 'Raw Def. (Low to High)', direction: 'asc' },
  { value: 'dme-desc', label: 'DME (High to Low)', direction: 'desc' },
  { value: 'dme-asc', label: 'DME (Low to High)', direction: 'asc' },
  { value: 'modDef-desc', label: 'Mod. Def. (High to Low)', direction: 'desc' },
  { value: 'modDef-asc', label: 'Mod. Def. (Low to High)', direction: 'asc' },
  { value: 'generals-desc', label: 'Generals (High to Low)', direction: 'desc' },
  { value: 'generals-asc', label: 'Generals (Low to High)', direction: 'asc' },
  { value: 'freshness-desc', label: 'Freshness (Newest First)', direction: 'desc' },
  { value: 'freshness-asc', label: 'Freshness (Oldest First)', direction: 'asc' },
] as const satisfies readonly SortOptionDef[];

export type MilitarySortValue = (typeof MILITARY_SORT_OPTIONS)[number]['value'];

const MILITARY_ACCESSORS: Record<MilitarySortValue, Accessor> = {
  'province-asc': (r) => r.provinceName,
  'land-desc': (r) => r.land,
  'land-asc': (r) => r.land,
  'nwpa-desc': (r) => r.nwpa,
  'nwpa-asc': (r) => r.nwpa,
  'rawOff-desc': (r) => som(r)?.offensePoints ?? null,
  'rawOff-asc': (r) => som(r)?.offensePoints ?? null,
  'ome-desc': (r) => som(r)?.offensiveME ?? null,
  'ome-asc': (r) => som(r)?.offensiveME ?? null,
  'modOff-desc': (r) => (r.modifiedOffense ? fuzzyMidpointDouble(r.modifiedOffense) : null),
  'modOff-asc': (r) => (r.modifiedOffense ? fuzzyMidpointDouble(r.modifiedOffense) : null),
  'rawDef-desc': (r) => som(r)?.defensePoints ?? null,
  'rawDef-asc': (r) => som(r)?.defensePoints ?? null,
  'dme-desc': (r) => som(r)?.defensiveME ?? null,
  'dme-asc': (r) => som(r)?.defensiveME ?? null,
  'modDef-desc': (r) => (r.modifiedDefense ? fuzzyMidpointDouble(r.modifiedDefense) : null),
  'modDef-asc': (r) => (r.modifiedDefense ? fuzzyMidpointDouble(r.modifiedDefense) : null),
  'generals-desc': (r) => som(r)?.generals ?? null,
  'generals-asc': (r) => som(r)?.generals ?? null,
  'freshness-desc': (r) => latestReportTick(r),
  'freshness-asc': (r) => latestReportTick(r),
};

export type DossierSortTab = 'resources' | 'military';

function optionsFor(tab: DossierSortTab): readonly SortOptionDef[] {
  return tab === 'resources' ? RESOURCES_SORT_OPTIONS : MILITARY_SORT_OPTIONS;
}

function accessorsFor(tab: DossierSortTab): Record<string, Accessor> {
  return tab === 'resources' ? RESOURCES_ACCESSORS : MILITARY_ACCESSORS;
}

/**
 * Comparator for a combined key+direction sort value. Nulls always sort last, regardless of
 * `direction`. Native `Array.prototype.sort` is stable (ES2019+), so equal/tied values preserve
 * their original relative order.
 */
export function compareDossierRows(a: DossierProvinceRow, b: DossierProvinceRow, tab: DossierSortTab, value: string): number {
  const option = optionsFor(tab).find((o) => o.value === value);
  const accessor = accessorsFor(tab)[value];
  if (!option || !accessor) return 0;

  const av = accessor(a);
  const bv = accessor(b);

  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;

  const cmp = typeof av === 'string' && typeof bv === 'string'
    ? av.localeCompare(bv)
    : (av as number) - (bv as number);

  return option.direction === 'asc' ? cmp : -cmp;
}

/** Returns a new sorted array (input is not mutated). */
export function sortDossierRows(rows: DossierProvinceRow[], tab: DossierSortTab, value: string): DossierProvinceRow[] {
  return [...rows].sort((a, b) => compareDossierRows(a, b, tab, value));
}
